import React, { useEffect, useState } from 'react';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import { C, ACCENT } from '../../theme';
import { Upload, Download } from 'lucide-react';

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(80,120,160,0.25)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }} onClick={onClose}>
      <div style={{...C.modal, width:520}} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:'#1a2a3a' }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#8aa0b8', fontSize:22, cursor:'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const BLANK = { name:'', universe:1887226, scope:'global', plat_comp:{} };

export default function AudiencesPage() {
  const { canEditCatalogue } = useAuth();
  const [audiences, setAudiences] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkVariant, setBulkVariant] = useState('info');
  const [bulkErrors, setBulkErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [modalTab, setModalTab] = useState('basic');

  const load = () => Promise.all([
    api.getAudiences(),
    api.getPlatforms()
  ]).then(([a,p])=>{ setAudiences(a); setPlatforms(p); }).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{ load(); },[]);

  function openNew() { setForm({...BLANK}); setModalTab('basic'); setModal('new'); }
  function openEdit(a) { setForm({ name:a.name, universe:a.universe||1887226, scope:a.scope||'global', plat_comp:a.plat_comp||{} }); setModalTab('basic'); setModal(a); }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (modal==='new') await api.createAudience(form);
      else await api.updateAudience(modal.id, form);
      await load(); setModal(null);
    } catch(e){ alert(e.message); } finally{ setSaving(false); }
  }

  async function downloadAudienceTemplate() {
    let XLSX;
    try { XLSX = await import('xlsx'); } catch(e) {}
    if (!XLSX) { alert('Excel library not available.'); return; }
    const wb = XLSX.utils.book_new();

    // Sheet 1: Audience Composition
    // Columns: Audience | Universe | Platform1 | Platform2 | ...
    const platLabels = platforms.map(p => p.name);
    const header = ['Audience', 'Universe', ...platLabels];
    const rows = [header];
    audiences.forEach(a => {
      const row = [a.name, a.universe];
      platforms.forEach(p => {
        const v = (a.plat_comp || {})[p.id];
        row.push(v !== undefined ? v : '');
      });
      rows.push(row);
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Audience Composition');

    // Sheet 2: Instructions
    const inst = [
      ['AUDIENCES GLOBAL DATABASE — BULK TEMPLATE'],[''],
      ['RULES:'],
      ['- Do NOT change Audience/Universe columns — used for matching'],
      ['- Platform columns: enter audience composition as decimal (e.g. 0.65 for 65%)'],
      ['- Leave cells blank to keep existing values'],
      ['- Add new rows for new audiences (Universe required)'],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(inst), 'Instructions');

    XLSX.writeFile(wb, 'Audiences_Global_' + new Date().toISOString().slice(0,10) + '.xlsx');
  }

  async function handleBulkImport(e) {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true); setBulkStatus('Parsing…'); setBulkVariant('info'); setBulkErrors([]);
    let XLSX;
    try { XLSX = await import('xlsx'); } catch(err) {}
    if (!XLSX) { setBulkStatus('Excel library not available'); setBulkVariant('danger'); setImporting(false); return; }
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type:'array' });
    const errs = []; let updated = 0;
    const sheet = wb.Sheets['Audience Composition'];
    if (!sheet) { setBulkStatus('Sheet "Audience Composition" not found'); setBulkVariant('danger'); setImporting(false); return; }
    const rows = XLSX.utils.sheet_to_json(sheet, { header:1, defval:'' });
    if (rows.length < 2) { setBulkStatus('No data rows found'); setBulkVariant('danger'); setImporting(false); return; }
    const hdr = rows[0]; // ['Audience','Universe', ...platformNames]
    const platNames = hdr.slice(2);

    for (const row of rows.slice(1)) {
      const audName = String(row[0]||'').trim();
      if (!audName) continue;
      const aud = audiences.find(a => a.name.toLowerCase().trim() === audName.toLowerCase().trim());
      if (!aud) { errs.push(`Audience "${audName}" not found — skipped`); continue; }
      const newPlatComp = { ...(aud.plat_comp || {}) };
      platNames.forEach((platName, ci) => {
        const val = row[ci + 2];
        if (val === '' || val === null || val === undefined) return;
        const plat = platforms.find(p => p.name.toLowerCase().trim() === String(platName).toLowerCase().trim());
        if (!plat) return;
        newPlatComp[plat.id] = parseFloat(val);
      });
      try {
        await api.updateAudience(aud.id, { plat_comp: newPlatComp });
        updated++;
      } catch(err) { errs.push(`Failed to update "${audName}"`); }
    }
    await load();
    setBulkErrors(errs);
    setBulkVariant(errs.length ? 'warning' : 'success');
    setBulkStatus(`Updated ${updated} audiences${errs.length ? ' with '+errs.length+' warnings' : ''}`);
    setImporting(false);
    e.target.value = '';
  }

  const filtered = audiences.filter(a =>
    a.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={C.page}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:28 }}>
        <div>
          <h1 style={C.title}>Audiences <span style={{ color:'#8aa0b8', fontWeight:400, fontSize:16 }}>({audiences.length})</span></h1>
          <div style={{ fontSize:12, color:'#8aa0b8', marginTop:4 }}>Global audience database — available across all clients and plans</div>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <input placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)}
            style={{ padding:'8px 14px', border:'1px solid #c8d8e8', borderRadius:8, fontSize:13, outline:'none', width:200, background:'rgba(255,255,255,0.7)' }}/>
          <button style={{...C.btnSecondary, display:'inline-flex', alignItems:'center', gap:5}} onClick={downloadAudienceTemplate}><Download size={13}/>Export Template</button>
          {canEditCatalogue && <>
            <label style={{...C.btnSecondary, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:5}}>
              {importing ? 'Importing…' : <><Upload size={13}/>Bulk Import</>}
              <input type="file" accept=".xlsx,.xls" style={{display:'none'}} disabled={importing} onChange={handleBulkImport}/>
            </label>
            <button style={C.btnPrimary} onClick={openNew}>+ New Audience</button>
          </>}
        </div>
      </div>

      {loading ? <div style={C.empty}>Loading…</div> : (
        <table style={C.table}>
          <thead>
            <tr>
              <th style={C.th}>Audience Name</th>
              <th style={C.th}>Universe</th>
              <th style={C.th}>Scope</th>
              <th style={C.th}>Platform Overrides</th>
              {canEditCatalogue && <th style={C.th}></th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length===0 ? (
              <tr><td colSpan={5} style={{...C.td, textAlign:'center', color:'#8aa0b8', padding:48}}>
                {search ? 'No audiences match.' : 'No audiences yet.'}
              </td></tr>
            ) : filtered.map(a=>(
              <tr key={a.id} style={{...C.tr, cursor: canEditCatalogue ? 'pointer' : 'default'}}
                onClick={()=>canEditCatalogue&&openEdit(a)}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(0,144,208,0.04)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <td style={{...C.td, fontWeight:700}}>{a.name}</td>
                <td style={C.td}>{(a.universe||0).toLocaleString()}</td>
                <td style={C.td}><span style={C.mutedLabel()}>{a.scope||'global'}</span></td>
                <td style={{...C.td, color:'#6a8aaa'}}>
                  {Object.keys(a.plat_comp||{}).length > 0
                    ? `${Object.keys(a.plat_comp).length} platform overrides`
                    : '—'}
                </td>
                {canEditCatalogue && (
                  <td style={{...C.td, textAlign:'right'}} onClick={e=>e.stopPropagation()}>
                    <button style={C.btnSecondary} onClick={()=>openEdit(a)}>Edit</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {bulkStatus && (
        <div style={{ ...C.feedback(bulkVariant), marginTop:12, marginBottom:0, fontSize:12 }}>{bulkStatus}</div>
      )}
      {bulkErrors.length > 0 && (
        <div style={{ ...C.feedback('warning'), marginTop:8, marginBottom:0, fontSize:11, maxHeight:100, overflowY:'auto' }}>
          {bulkErrors.map((e,i) => <div key={i}>{e}</div>)}
        </div>
      )}
      {modal && (
        <Modal title={modal==='new'?'New Audience':`Edit: ${modal.name}`} onClose={()=>setModal(null)}>
          {/* Tabs */}
          <div style={{ display:'flex', gap:2, marginBottom:20, borderBottom:'1px solid #dce8f0' }}>
            {[['basic','Basic Info'],['composition','Platforms Audience Composition']].map(([t,l])=>(
              <button key={t} onClick={()=>setModalTab(t)} style={{
                padding:'6px 14px', fontSize:12, fontWeight:600, cursor:'pointer',
                background:'transparent', border:'none',
                borderBottom: modalTab===t ? `2px solid ${ACCENT}` : '2px solid transparent',
                color: modalTab===t ? ACCENT : '#8aa0b8',
              }}>{l}</button>
            ))}
          </div>

          {modalTab==='basic' && <>
            <label style={{display:'block', marginBottom:14}}>
              <span style={C.label}>Audience Name *</span>
              <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Women 18-45 Urban" style={C.input} autoFocus/>
            </label>
            <label style={{display:'block', marginBottom:14}}>
              <span style={C.label}>Universe (total people)</span>
              <input type="number" value={form.universe} onChange={e=>setForm(p=>({...p,universe:+e.target.value}))} style={C.input}/>
            </label>
            <label style={{display:'block', marginBottom:14}}>
              <span style={C.label}>Scope</span>
              <select value={form.scope} onChange={e=>setForm(p=>({...p,scope:e.target.value}))} style={C.input}>
                <option value="global">Global (all orgs)</option>
                <option value="org">Organization</option>
                <option value="client">Client</option>
              </select>
            </label>
          </>}

          {modalTab==='composition' && <>
            <div style={{ fontSize:12, color:'#8aa0b8', marginBottom:12 }}>
              Set the share of this audience reached by each platform. Leave blank to use platform default.
            </div>
            <div style={{ maxHeight:320, overflowY:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    <th style={{...C.th, textAlign:'left'}}>Platform</th>
                    <th style={{...C.th, width:120, fontSize:10, whiteSpace:'nowrap'}}>Aud. Comp</th>
                  </tr>
                </thead>
                <tbody>
                  {platforms.map(p=>(
                    <tr key={p.id} style={{ borderBottom:'1px solid #eef4f8' }}>
                      <td style={{...C.td, fontSize:12}}>
                        <div style={{ fontWeight:600 }}>{p.name}</div>
                        <div style={{ fontSize:11, color:'#8aa0b8' }}>{p.seller||''}</div>
                      </td>
                      <td style={C.td}>
                        <input
                          type="number" step="0.01" min="0" max="1"
                          placeholder={`${(p.audience_comp||0.65).toFixed(2)} (default)`}
                          value={form.plat_comp[p.id]!==undefined ? form.plat_comp[p.id] : ''}
                          onChange={e=>{
                            const val=e.target.value;
                            setForm(prev=>({
                              ...prev,
                              plat_comp: val===''||val===undefined
                                ? Object.fromEntries(Object.entries(prev.plat_comp).filter(([k])=>k!==String(p.id)))
                                : {...prev.plat_comp, [p.id]: val}
                            }));
                          }}
                          onBlur={e=>{
                            const val=parseFloat(e.target.value);
                            if(!isNaN(val))setForm(prev=>({...prev,plat_comp:{...prev.plat_comp,[p.id]:val}}));
                          }}
                          style={{...C.input, padding:'4px 8px', fontSize:12, width:100}}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>}

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
            <button style={C.btnSecondary} onClick={()=>setModal(null)}>Cancel</button>
            <button style={C.btnPrimary} onClick={handleSave} disabled={saving}>{saving?'Saving…':modal==='new'?'Create':'Save'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
